import React, { useEffect, useState, useRef } from "react";
import { api, inr } from "../lib/api";
import { useAuth } from "../lib/auth";
import Shell, { PageHeader } from "../components/Shell";
import SkuAliasModal from "../components/SkuAliasModal";
import { MagnifyingGlass, PencilSimple, X, Check, DownloadSimple, UploadSimple, Stack, Funnel, TreeStructure } from "@phosphor-icons/react";

export default function SkuMaster() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [aliasOpen, setAliasOpen] = useState(false);
  const [masterFilter, setMasterFilter] = useState("");
  const [masters, setMasters] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 50 });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState({});  // sku -> true
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState("selection"); // selection | filter
  const emptyBulk = { category: "", subcategory: "", hsn: "", gst_rate: "", mrp: "", cost_price: "", selling_price: "", reorder_level: "", master_sku: "" };
  const [bulkUpd, setBulkUpd] = useState(emptyBulk);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/warehouse/skus?q=${encodeURIComponent(q)}&master_sku=${encodeURIComponent(masterFilter)}&page=${page}&limit=50`);
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [page, masterFilter]);
  useEffect(() => { api.get("/warehouse/master-skus").then((r) => setMasters(r.data)).catch(() => {}); }, [data.total]);

  const search = (e) => { e.preventDefault(); setPage(1); load(); };
  const allChecked = data.items.length > 0 && data.items.every((s) => selected[s.sku]);
  const someChecked = Object.keys(selected).length > 0;

  const toggleOne = (sku) => {
    const c = { ...selected };
    if (c[sku]) delete c[sku]; else c[sku] = true;
    setSelected(c);
  };
  const toggleAll = () => {
    if (allChecked) {
      const c = { ...selected };
      for (const it of data.items) delete c[it.sku];
      setSelected(c);
    } else {
      const c = { ...selected };
      for (const it of data.items) c[it.sku] = true;
      setSelected(c);
    }
  };

  const saveEdit = async () => {
    const body = {};
    for (const k of ["category", "subcategory", "hsn", "gst_rate", "mrp", "cost_price", "selling_price", "reorder_level", "master_sku"]) {
      const v = editing[k];
      if (k === "master_sku") { if (isAdmin && v !== undefined) body[k] = v; continue; }
      if (v !== undefined && v !== "") body[k] = ["gst_rate", "mrp", "cost_price", "selling_price"].includes(k) ? parseFloat(v) : (k === "reorder_level" ? parseInt(v) : v);
    }
    try {
      await api.patch(`/warehouse/skus/${encodeURIComponent(editing.sku)}`, body);
      setEditing(null); load();
    } catch (e) { alert("Save failed: " + (e.response?.data?.detail || e.message)); }
  };

  const applyBulk = async () => {
    const upd = {};
    for (const k of Object.keys(bulkUpd)) {
      const v = bulkUpd[k];
      if (v === "" || v === null || v === undefined) continue;
      upd[k] = ["gst_rate", "mrp", "cost_price", "selling_price"].includes(k) ? parseFloat(v) : (k === "reorder_level" ? parseInt(v) : v);
    }
    if (Object.keys(upd).length === 0) return alert("Nothing to update");
    try {
      if (bulkMode === "selection") {
        const skus = Object.keys(selected);
        if (skus.length === 0) return alert("No SKUs selected");
        const r = await api.post("/warehouse/skus/bulk", { skus, updates: upd });
        alert(`Bulk update applied. Matched: ${r.data.matched}, Modified: ${r.data.modified}`);
      } else {
        const r = await api.post("/warehouse/skus/bulk-by-filter", {
          q, category: "", updates: upd,
        });
        alert(`Filter-based update applied to ${r.data.matched} SKUs. Modified: ${r.data.modified}`);
      }
      setBulkOpen(false); setBulkUpd(emptyBulk);
      setSelected({}); load();
    } catch (e) { alert("Bulk update failed: " + (e.response?.data?.detail || e.message)); }
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("pos_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/warehouse/skus/export.csv?q=${encodeURIComponent(q)}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `skus_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const onImportClick = () => fileInputRef.current?.click();
  const onFileChosen = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setImporting(true); setImportResult(null);
    const form = new FormData(); form.append("file", f);
    try {
      const token = localStorage.getItem("pos_token");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/warehouse/skus/import`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Import failed");
      setImportResult(data); load();
    } catch (er) { alert("Import failed: " + er.message); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.limit || 50)));

  return (
    <Shell>
      <PageHeader title="SKU Master" subtitle="Synced from Main Warehouse. Edit categorization, HSN, GST, pricing & reorder level." testId="skus-header"
        actions={<>
          {isAdmin && <button onClick={() => setAliasOpen(true)} data-testid="alias-mapping-btn" className="inline-flex items-center gap-1 h-10 px-3 bg-slate-900 text-white text-sm font-semibold rounded-md hover:bg-slate-800"><TreeStructure size={14} weight="bold" />Tally Code Mapping</button>}
          <button onClick={exportCsv} data-testid="export-skus-btn" className="inline-flex items-center gap-1 h-10 px-3 bg-white border border-slate-300 text-sm font-semibold rounded-md hover:bg-slate-50"><DownloadSimple size={14} weight="bold" />Export CSV</button>
          <button onClick={onImportClick} disabled={importing} data-testid="import-skus-btn" className="inline-flex items-center gap-1 h-10 px-3 bg-white border border-slate-300 text-sm font-semibold rounded-md hover:bg-slate-50 disabled:opacity-50"><UploadSimple size={14} weight="bold" />{importing ? "Importing…" : "Import CSV"}</button>
          <input type="file" ref={fileInputRef} accept=".csv" onChange={onFileChosen} className="hidden" />
        </>}
      />
      <div className="p-8 space-y-4">
        {importResult && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 px-4 py-3 rounded-md text-sm" data-testid="import-result">
            <b>Import complete.</b> Updated: <span className="font-mono-num">{importResult.updated}</span> SKUs. {importResult.errors.length > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer text-amber-700 font-semibold">{importResult.errors.length} errors (click to see)</summary>
                <pre className="text-[11px] mt-2 max-h-40 overflow-auto bg-white p-2 rounded">{JSON.stringify(importResult.errors, null, 2)}</pre>
              </details>
            )}
            <button onClick={() => setImportResult(null)} className="float-right text-xs font-semibold text-emerald-700 hover:text-emerald-900">×</button>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <form onSubmit={search} className="flex gap-2 flex-1 min-w-[280px] max-w-md">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="skus-search" placeholder="Search SKU, name or barcode…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full h-11 pl-9 pr-3 border border-slate-300 bg-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
            <button type="submit" className="h-11 px-5 bg-slate-900 text-white font-semibold rounded-md text-sm hover:bg-slate-800">Search</button>
          </form>
          {someChecked && (
            <button onClick={() => { setBulkMode("selection"); setBulkOpen(true); }} data-testid="bulk-edit-selection-btn" className="inline-flex items-center gap-1.5 h-11 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md">
              <Stack size={15} weight="bold" />Bulk edit {Object.keys(selected).length}
            </button>
          )}
          <select value={masterFilter} onChange={(e) => { setPage(1); setMasterFilter(e.target.value); }} data-testid="master-sku-filter" className="h-11 px-3 border border-slate-300 bg-white rounded-md text-sm max-w-[260px]">
            <option value="">Master SKU: All</option>
            {masters.map((m) => <option key={m.master_sku} value={m.master_sku}>{m.master_sku} ({m.variants})</option>)}
          </select>
          {q && (
            <button onClick={() => { setBulkMode("filter"); setBulkOpen(true); }} data-testid="bulk-edit-filter-btn" className="inline-flex items-center gap-1.5 h-11 px-4 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold rounded-md">
              <Funnel size={15} weight="bold" />Bulk edit by filter
            </button>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="skus-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="px-3 py-3"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="select-all" /></th>
                  <th className="text-left px-4 py-3 font-semibold">Thumb</th>
                  <th className="text-left px-4 py-3 font-semibold">SKU</th>
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Master SKU</th>
                  <th className="text-left px-4 py-3 font-semibold">Category</th>
                  <th className="text-left px-4 py-3 font-semibold">HSN</th>
                  <th className="text-right px-4 py-3 font-semibold">GST %</th>
                  <th className="text-right px-4 py-3 font-semibold">MRP</th>
                  <th className="text-right px-4 py-3 font-semibold">Selling</th>
                  <th className="text-right px-4 py-3 font-semibold">Reorder</th>
                  <th className="text-right px-4 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={12} className="text-center py-12 text-slate-400">Loading…</td></tr>}
                {!loading && data.items.length === 0 && <tr><td colSpan={11} className="text-center py-12 text-slate-400">No SKUs. Sync warehouse from Overview.</td></tr>}
                {!loading && data.items.map((s) => (
                  <tr key={s.sku} className={`border-b border-slate-100 ${selected[s.sku] ? "bg-emerald-50/30" : "hover:bg-slate-50"}`}>
                    <td className="px-3 py-2"><input type="checkbox" checked={!!selected[s.sku]} onChange={() => toggleOne(s.sku)} data-testid={`select-${s.sku}`} /></td>
                    <td className="px-4 py-2">{s.thumbnail ? <img src={s.thumbnail} alt="" className="w-10 h-10 rounded-sm object-cover border border-slate-200" /> : <div className="w-10 h-10 rounded-sm bg-slate-100" />}</td>
                    <td className="px-4 py-2 font-mono-num text-slate-900">{s.sku}</td>
                    <td className="px-4 py-2">{s.name}</td>
                    <td className="px-4 py-2 text-xs" data-testid={`master-${s.sku}`}>{s.master_sku ? <button onClick={() => { setPage(1); setMasterFilter(s.master_sku); }} className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 font-semibold hover:bg-indigo-100">{s.master_sku}</button> : <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-2 text-slate-700">{s.category || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-2 font-mono-num">{s.hsn || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-2 text-right font-mono-num">{(s.gst_rate || 0).toFixed(1)}</td>
                    <td className="px-4 py-2 text-right font-mono-num">{inr(s.mrp)}</td>
                    <td className="px-4 py-2 text-right font-mono-num text-emerald-700">{inr(s.selling_price)}</td>
                    <td className="px-4 py-2 text-right font-mono-num">{s.reorder_level ?? <span className="text-slate-400">5</span>}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => setEditing({ ...s })} data-testid={`edit-sku-${s.sku}`} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900"><PencilSimple size={14} /> Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 text-xs text-slate-500">
            <div>Page <b>{data.page}</b> of <b>{totalPages}</b> · <span className="font-mono-num">{data.total}</span> total {someChecked && <>· <span className="text-emerald-700 font-semibold">{Object.keys(selected).length} selected</span></>}</div>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(page - 1)} className="h-8 px-3 border border-slate-300 rounded-md disabled:opacity-40">Prev</button>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="h-8 px-3 border border-slate-300 rounded-md disabled:opacity-40">Next</button>
            </div>
          </div>
        </div>
      </div>

      {/* Single SKU edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-md shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="edit-sku-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <div className="font-display font-bold text-lg">Edit SKU</div>
                <div className="text-xs text-slate-500 font-mono-num mt-0.5">{editing.sku}</div>
              </div>
              <button onClick={() => setEditing(null)} className="p-2 hover:bg-slate-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              {[
                ["category", "Category", "text", "col-span-2"],
                ["subcategory", "Subcategory", "text", "col-span-2"],
                ["hsn", "HSN Code", "text"],
                ["gst_rate", "GST %", "number"],
                ["mrp", "MRP", "number"],
                ["cost_price", "Cost Price", "number"],
                ["selling_price", "Selling Price", "number"],
                ["reorder_level", "Reorder Level", "number"],
                ...(isAdmin ? [["master_sku", "Master SKU (parent product) — admin only", "text", "col-span-2"]] : []),
              ].map(([k, label, type, cls]) => (
                <div key={k} className={cls || ""}>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">{label}</label>
                  <input type={type} step="0.01" list={k === "master_sku" ? "master-sku-options" : undefined} value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} data-testid={`edit-field-${k}`} className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
                </div>
              ))}
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="h-10 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
              <button onClick={saveEdit} data-testid="save-sku-btn" className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md">Save</button>
            </div>
          </div>
        </div>
      )}

      <datalist id="master-sku-options">{masters.map((m) => <option key={m.master_sku} value={m.master_sku} />)}</datalist>
      {aliasOpen && <SkuAliasModal onClose={() => setAliasOpen(false)} />}

      {/* Bulk modal */}
      {bulkOpen && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-4" onClick={() => setBulkOpen(false)}>
          <div className="bg-white rounded-md shadow-2xl w-full max-w-xl" onClick={(e) => e.stopPropagation()} data-testid="bulk-modal">
            <div className="px-5 py-4 border-b border-slate-200">
              <div className="font-display font-bold text-lg">Bulk Update SKUs</div>
              <div className="text-xs text-slate-500 mt-1">
                {bulkMode === "selection"
                  ? <>Will apply to <b className="text-emerald-700 font-mono-num">{Object.keys(selected).length} selected SKUs</b>. Leave any field blank to skip.</>
                  : <>Will apply to <b className="text-amber-700">all SKUs matching the current search "<span className="font-mono-num">{q}</span>"</b>. Leave any field blank to skip.</>}
              </div>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              {[
                ["category", "Category", "text", "col-span-2"],
                ["hsn", "HSN Code", "text"],
                ["gst_rate", "GST %", "number"],
                ["mrp", "MRP", "number"],
                ["cost_price", "Cost Price", "number"],
                ["selling_price", "Selling Price", "number"],
                ["reorder_level", "Reorder Level", "number"],
                ...(isAdmin ? [["master_sku", "Master SKU (parent product) — admin only", "text", "col-span-2"]] : []),
              ].map(([k, label, type, cls]) => (
                <div key={k} className={cls || ""}>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">{label}</label>
                  <input type={type} step="0.01" list={k === "master_sku" ? "master-sku-options" : undefined} value={bulkUpd[k]} onChange={(e) => setBulkUpd({ ...bulkUpd, [k]: e.target.value })} data-testid={`bulk-field-${k}`} placeholder="Leave blank to skip" className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
              ))}
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setBulkOpen(false)} className="h-10 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
              <button onClick={applyBulk} data-testid="apply-bulk-btn" className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md inline-flex items-center gap-1"><Check size={14} weight="bold" />Apply Bulk Update</button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
